import { Group, MeshBasicMaterial, Object3D, Vector3 } from "three";
import IsoBlock from "./IsoBlock";
import animate from "./animate";
import { easeInCubic, easeInOutCubic, easeOutCubic } from "@shared/lib/animation/easing";
import delay from "./delay";

export interface IsoTaxGraphOptions {
  color: string,
}

export default class IsoTaxGraph {
  group: Group;
  blocks: Array<IsoBlock>;
  taxBlocks: Array<IsoBlock>;
  heights = [
    4,
    5,
    4.5,
    5.5,
    4,
  ];
  sequenceDelay = 150;

  constructor (parent: Object3D, options: IsoTaxGraphOptions) {
    this.group = new Group();

    this.blocks = Array(this.heights.length).fill(0)
      .map((_, i) => {
        const block = new IsoBlock(this.group, {
          color: options.color,
          position: new Vector3(i * 2, 0, 0),
        });
        block.height = i === 0 ? 1 : 0;

        return block;
      });

    this.taxBlocks = this.blocks.map((block) => {
      const taxBlock = new IsoBlock(this.group, {
        color: options.color,
        position: block.mesh.position,
        material: new MeshBasicMaterial({
          color: (block.material as MeshBasicMaterial).color,
          reflectivity: 1,
          transparent: true,
          opacity: 0,
        }),
      });
      taxBlock.height = 0;

      return taxBlock;
    });

    parent.add(this.group);
  }

  async animateIn (render: () => void): Promise<void> {
    const upDuration = 750;

    this.blocks.forEach(async (block, i) => {
      await delay(i * this.sequenceDelay);

      const startHeight = block.height;

      animate((t) => {
        block.height = startHeight + (this.heights[i] - startHeight) * t;
        render();
      }, {
        duration: upDuration,
        easing: easeOutCubic,
      });
    });

    await delay(this.blocks.length * this.sequenceDelay + upDuration);
  }

  async animateTaxIn (render: () => void): Promise<void> {
    const downDuration = 1000;

    this.blocks.forEach(async (block, i) => {
      await delay(i * 150);

      const taxBlock = this.taxBlocks[i];
      const height = this.heights[i];

      animate((t) => {
        block.height = (1 - t * 0.5) * height;

        taxBlock.y = height - height * 0.5 * t;
        taxBlock.height = height * 0.5 * t;
        taxBlock.material.opacity = t * 0.75;
        render();
      }, {
        duration: downDuration,
        easing: easeInOutCubic,
      });
    });

    await delay(this.blocks.length * this.sequenceDelay + downDuration);
  }

  async animateTaxChange ({
    startPercent,
    endPercent,
  }: { startPercent: number,
    endPercent: number, }, render: () => void): Promise<void> {
    const duration = 1000;

    await Promise.all([
      this.blocks.map((block, i) => {
        const taxBlock = this.taxBlocks[i];
        const height = this.heights[i];

        const taxStartPercent = 1 - startPercent;
        const taxEndPercent = 1 - endPercent;

        return animate((t) => {
          block.height = (startPercent + t * (endPercent - startPercent)) * height;

          taxBlock.y = height - (taxStartPercent + t * (taxEndPercent - taxStartPercent)) * height;
          taxBlock.height = (taxStartPercent + t * (taxEndPercent - taxStartPercent)) * height;
          render();
        }, {
          duration,
          easing: easeInOutCubic,
        });
      }),
    ]);
  }

  async animateOut (render: () => void): Promise<void> {
    const downDuration = 750;

    this.blocks.forEach(async (block, i, arr) => {
      await delay(i * this.sequenceDelay);

      const isLast = i === arr.length - 1;
      const taxBlock = this.taxBlocks[i];
      const fromHeight = block.height;
      const toHeight = isLast ? 1 : 0;
      const fromTaxBlockY = taxBlock.y;
      const toTaxBlockY = isLast ? 1 : 0;
      const taxBlockHeight = taxBlock.height;

      animate((t) => {
        block.height = fromHeight + (toHeight - fromHeight) * t;
        taxBlock.y = fromTaxBlockY + (toTaxBlockY - fromTaxBlockY) * t;
        taxBlock.height = taxBlockHeight - t * taxBlockHeight;

        render();
      }, {
        duration: downDuration,
        easing: isLast ? easeInOutCubic : easeInCubic,
      });
    });

    await delay(this.blocks.length * this.sequenceDelay + downDuration);
  }

  dispose () {
    this.group.removeFromParent();
    this.blocks.forEach((block) => block.dispose());
    this.blocks.length = 0;
  }
}
